import { useCallback } from 'react';
import { useToast } from '@cad-arc';
import type { SensorAlertBundle, SensorsResource } from '@mark43/cad-api';
import { useApiClient } from '~/api-client';
import { useSensorsStore } from './use-sensors-store';

export type ArchiveSensorAlerts = (alerts: SensorAlertBundle[], archived: boolean) => void;
export type ArchiveSensorAlert = (alert: SensorAlertBundle, isArchived: boolean) => void;

/** How long the undo action stays up. The design asks for about five seconds. */
const UNDO_TOAST_DURATION_MS = 5000;

/**
 * Archives or restores sensor events, per user, server-side — any number in one request.
 *
 * Batched because the endpoint takes a list: "Archive all" is one call, not one per row. Queued
 * through `SensorsStore.enqueueArchiveWrite`, which applies the change optimistically and sends
 * writes strictly one at a time; see there for why concurrent writes corrupt the archived set.
 *
 * Takes the desired state rather than a verb because the endpoint is idempotent in both
 * directions, so the same call archives and restores. The response is the user's whole archived
 * set, and it replaces ours.
 */
export function useArchiveSensorAlerts(): ArchiveSensorAlerts {
    const apiClient = useApiClient();
    const sensorsStore = useSensorsStore();
    const toast = useToast();

    return useCallback(
        function archiveSensorAlerts(alerts: SensorAlertBundle[], archived: boolean) {
            const alertKeys = [...new Set(alerts.map((alert) => alert.alertKey))];
            if (alertKeys.length === 0) {
                return;
            }
            void sensorsStore.enqueueArchiveWrite(alertKeys, archived, async function send() {
                let response;
                try {
                    response = await apiClient.request<SensorsResource.SetSensorAlertArchived>({
                        method: 'POST',
                        url: 'sensors/alerts/archive',
                        data: { alertKeys, archived },
                        /*
                         * Silent because this owns its failure message. The API client only toasts
                         * a 400 or a 207, so a 500, a 404 or a dropped connection would otherwise
                         * roll the rows back with no word at all — right after the undo toast said
                         * it had worked.
                         */
                        shouldFailSilently: true,
                    });
                } catch (error) {
                    const noun =
                        alertKeys.length === 1 ? 'the sensor event' : `${alertKeys.length} sensor events`;
                    toast({
                        status: 'error',
                        description: `Couldn't ${archived ? 'archive' : 'restore'} ${noun}. ${
                            alertKeys.length === 1 ? 'It is' : 'They are'
                        } back where ${alertKeys.length === 1 ? 'it was' : 'they were'}.`,
                    });
                    // Rethrown so the queue rolls the rows back.
                    throw error;
                }
                return response.data.data?.archivedAlertKeys;
            });
        },
        [apiClient, sensorsStore, toast]
    );
}

/** The single-row form, for the surfaces that act on one alert at a time. */
export function useArchiveSensorAlert(): ArchiveSensorAlert {
    const archiveSensorAlerts = useArchiveSensorAlerts();
    return useCallback(
        function archiveSensorAlert(alert: SensorAlertBundle, isArchived: boolean) {
            archiveSensorAlerts([alert], isArchived);
        },
        [archiveSensorAlerts]
    );
}

/**
 * Archive or restore, followed by a toast that can take it back.
 *
 * The row leaves its tab on the click, so the undo is the only way back to it without switching
 * tabs — which is why it is offered on every archive, including "Archive all".
 */
export function useArchiveSensorAlertsWithUndo(): ArchiveSensorAlerts {
    const archiveSensorAlerts = useArchiveSensorAlerts();
    const toast = useToast();

    return useCallback(
        function archiveSensorAlertsWithUndo(alerts: SensorAlertBundle[], archived: boolean) {
            if (alerts.length === 0) {
                return;
            }
            archiveSensorAlerts(alerts, archived);

            const noun = alerts.length === 1 ? 'sensor event' : `${alerts.length} sensor events`;
            toast({
                status: 'default',
                duration: UNDO_TOAST_DURATION_MS,
                description: `${archived ? 'Archived' : 'Restored'} ${noun}`,
                action: {
                    children: 'Undo',
                    onClick() {
                        archiveSensorAlerts(alerts, !archived);
                    },
                },
            });
        },
        [archiveSensorAlerts, toast]
    );
}
